/* @flow */
/**
 * Manages GAC reason use-case configuration data, filters, and related API updates.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import { toggleSpinner } from '../dialogStates/dialogStatesSlice';
import getflattenResponse, { cryptoRand } from '../../helpers/common';
import { getFilteredResults, getFilterDropdownValuesFromResponse } from '../../helpers/dataHelpers';
import sortFilterData from './gacReasonUsecaseHelper';
import gacReasonUsecasesMock from '../mock/gacReasonUsecasesMock';

export const name = 'gacReasonUseCases';

function createInitialState() {
  return {
    filterDropdownValues: {
    },
    selectedFilters: {
      usecaseGacReasonCode: [],
      divType: [],
      isActive: []
    },
    dataId: '',
    settingsFilterId: '',
    SettingsData: [],
    pmoRequestBody: null,
    dataRows: gacReasonUsecasesMock,
    gacReasonUseCasesData: null,
    unfilteredConfigScreenData: null
  };
}

export const initialState = createInitialState();

// UPDATE_GAC_REASON_USE_CASES_SCREEN_RESULTS
/**
 * Reducer that stores fetched GAC reason use-case rows and their filtered view.
 */
function updateGacReasonCodeUsecasesResults(state: Object, action: Object) {
  const payload = {
    data: action.payload.updateddata,
    // Fall back to all fetched rows when no filter result set is supplied.
    filteredResults: action.payload.filteredResults || action.payload.updateddata
  };

  return {
    ...state,
    gacReasonUseCasesData: payload.filteredResults,
    unfilteredConfigScreenData: payload.data,
    // Refreshes table consumers that key off dataId after data replacement.
    dataId: cryptoRand().toString().slice(2, 10)
  };
}

// CLEAR_GAC_REASON_USE_CASES_FILTERS
/**
 * Reducer that clears selected filters and restores the unfiltered use-case rows.
 */
function clearGacReasonCodeUsecaseFilters(state: Object) {
  return {
    ...state,
    selectedFilters: {
      ...initialState.selectedFilters
    },
    gacReasonUseCasesData: state.unfilteredConfigScreenData
  };
}

// UPDATE_GAC_REASON_USE_CASES_FILTERED_RESULTS
/**
 * Reducer that stores filtered GAC reason use-case rows for the configuration screen.
 */
function updateFilteredGACUsecasesConfigScreen(state: Object, action: Object) {
  const payload = {
    data: action.payload
  };
  return {
    ...state,
    gacReasonUseCasesData: payload.data,
    dataId: cryptoRand().toString().slice(2, 10)
  };
}

// ADD_GAC_REASON_USE_CASES_FILTER_VALUES
/**
 * Reducer that updates selected filter values for a specific filter key.
 */
function updateFilterValues(state: Object, action: Object) {
  if (Array.isArray(action.payload.values) && action.payload.filter) {
    return {
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        [action.payload.filter]: action.payload.values
      }
    };
  }
  return { ...state };
}

// SETTINGS_UPDATE_FILTER_DROPDOWN_VALUES
/**
 * Reducer that stores sorted dropdown values for GAC reason use-case filters.
 */
function updateFilterDropdownValues(state: Object, action: Object) {
  if (
    action.payload && Object.keys(action.payload).length > 0
  ) {
    return {
      ...state,
      filterDropdownValues: sortFilterData(action.payload)
    };
  }
  return { ...state };
}

// SET_SETTINGS_FILTER_ID
/**
 * Reducer that refreshes the settings filter identifier after PMO filter changes.
 */
function updatePMORequestFilterId(state: Object) {
  return { ...state, settingsFilterId: cryptoRand().toString().slice(2, 10) };
}

/**
 * Builds the reducer map consumed by the GAC reason use-cases slice.
 */
function createReducers() {
  return {
    updateGacReasonCodeUsecasesResults,
    clearGacReasonCodeUsecaseFilters,
    updateFilteredGACUsecasesConfigScreen,
    updateFilterValues,
    updateFilterDropdownValues,
    updatePMORequestFilterId
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Async thunk that fetches GAC reason use-cases and derives UI rows and filter dropdown values.
 */
function getGacReasonCodeUsecasesResults() {
  return createAsyncThunk(
    `${name}/getGacReasonCodeUsecasesResults`,
    async (
      { callback },
      { getState, dispatch }
    ) => {
      dispatch(toggleSpinner(true));
      request({
        api: 'getReasonCodeUsecases',
        method: 'get'
      })
        .then((response) => {
          const state = getState();
          const flattenedResponse = getflattenResponse(response?.data?.objects);
          const updateddata = [];
          flattenedResponse.forEach((row) => {
            // Rename reasonCode for the UI field model and hide the raw key.
            const { reasonCode: _, ...modifiedRow } = row;
            modifiedRow.isActive = row.isActive ? 'Active' : 'Inactive';
            modifiedRow.usecaseGacReasonCode = row.reasonCode;
            updateddata.push(modifiedRow);
          });
          // Reapply any filters that were selected before the refresh.
          const { selectedFilters } = state.GacReasonUseCases;
          const filteredResults = getFilteredResults(
            updateddata,
            selectedFilters,
            'gacReasonUsecases'
          );
          dispatch(actions.updateGacReasonCodeUsecasesResults({
            updateddata,
            filteredResults
          }));
          dispatch(actions.updateFilterDropdownValues(
            getFilterDropdownValuesFromResponse(updateddata, [
              'usecaseGacReasonCode',
              'divType',
              'isActive'
            ], 'gacReasonUsecases')
          ));
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(response);
          }
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          console.log(error);
          callback(error);
        });
    }
  );
}

/**
 * Async thunk that posts new GAC reason use-case rows to AddGacUsecaseContacts.
 */
function addNewGacReasonCodeUsecasesDetails() {
  return createAsyncThunk(
    `${name}/addNewGacReasonCodeUsecasesDetails`,
    async (
      {
        data,
        callback
      },
      { dispatch }
    ) => {
      // Convert UI option objects into the backend gacData shape.
      const updatedData = data?.map((row) => ({
        reasonCode: row.usecaseGacReasonCode.value,
        reasonCodeDescription: row.usecaseGacReasonCode.label,
        divType: row.divType.value,
        useCaseCode: row.useCaseCode,
        useCaseDescription: row.useCaseDescription
      }));
      const usecaseRequestBody = {
        gacData: updatedData
      };
      dispatch(toggleSpinner(true));
      request({
        api: 'AddGacUsecaseContacts',
        method: 'post',
        data: usecaseRequestBody
      })
        .then((response) => {
          dispatch(toggleSpinner(false));
          callback(response.data);
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          callback(null, error);
        });
    }
  );
}

/**
 * Async thunk that sends updated GAC reason use-case rows to updateGacUsecaseContacts.
 */
function updateGacUsecaseDetails() {
  return createAsyncThunk(
    `${name}/updateGacUsecaseDetails`,
    async (
      {
        data,
        callback
      },
      { dispatch }
    ) => {
      if (data) {
        const updateRequestBody = {
          type: 'update',
          gacData: data
        };
        dispatch(toggleSpinner(true));
        request({
          api: 'updateGacUsecaseContacts',
          method: 'put',
          data: updateRequestBody
        })
          .then((response) => {
            dispatch(toggleSpinner(false));
            callback(response.data);
          })
          .catch((error) => {
            dispatch(toggleSpinner(false));
            callback(null, error);
          });
      }
    }
  );
}

/**
 * Async thunk that applies selected filters to the cached unfiltered use-case rows.
 */
function applyFilters() {
  return createAsyncThunk(
    `${name}/applyFilters`,
    async (
      { payload },
      { getState, dispatch }
    ) => {
      try {
        dispatch(actions.updateFilterValues(payload));
        const state = getState();
        const { selectedFilters, unfilteredConfigScreenData } = state.GacReasonUseCases;
        // Filter against the cached full dataset so each filter change remains reversible.
        const filteredResults = getFilteredResults(unfilteredConfigScreenData, selectedFilters, 'gacReasonUsecases');
        dispatch(actions.updateFilteredGACUsecasesConfigScreen(filteredResults));
      } catch (error) {
        console.log(error);
      }
    }
  );
}

/**
 * Async thunk that clears all selected GAC reason use-case filters.
 */
function clearFilters() {
  return createAsyncThunk(
    `${name}/clearFilters`,
    async (_, { dispatch }) => {
      dispatch(actions.clearGacReasonCodeUsecaseFilters());
    }
  );
}

/**
 * Async thunk that fetches all GAC reason codes and normalizes active status labels.
 */
function getGacReasonCodeResults() {
  return createAsyncThunk(
    `${name}/getGacReasonCodeResults`,
    (callback, { dispatch }) => {
      request({
        api: 'getGACReasonCode',
        method: 'get'
      })
        .then((response) => {
          const resData = response.data;
          resData.objects.forEach((row) => {
            // Normalize boolean activity flags into the labels expected by dropdowns.
            const updatedRow = row;
            updatedRow.isActive = row.isActive ? 'Active' : 'Inactive';
          });
          if (callback) callback(resData);
        })
        .catch((error) => {
          console.log('getGacReasonCodeResults->error', error);
          dispatch(toggleSpinner(false));
          if (callback) callback(error);
        });
    }
  );
}

/**
 * Async thunk that fetches active GAC reason codes from getGACReasonCodeActive.
 */
function getGACReasonCodeActiveResults() {
  return createAsyncThunk(
    `${name}/getGACReasonCodeActiveResults`,
    ({ callback }, { dispatch }) => {
      request({
        api: 'getGACReasonCodeActive',
        method: 'get'
      })
        .then((response) => {
          const resData = response.data;
          if (callback) callback(resData);
        })
        .catch((error) => {
          console.log('getGACReasonCodeActiveResults->error', error);
          dispatch(toggleSpinner(false));
          if (callback) callback(error);
        });
    }
  );
}

/**
 * Builds thunk action creators exported with the GAC reason use-cases slice actions.
 */
function createExtraActions() {
  return {
    getGacReasonCodeUsecasesResults: getGacReasonCodeUsecasesResults(),
    addNewGacReasonCodeUsecasesDetails: addNewGacReasonCodeUsecasesDetails(),
    updateGacUsecaseDetails: updateGacUsecaseDetails(),
    applyFilters: applyFilters(),
    clearFilters: clearFilters(),
    getGacReasonCodeResults: getGacReasonCodeResults(),
    getGACReasonCodeActiveResults: getGACReasonCodeActiveResults()
  };
}

export const extraActions = createExtraActions();

export const gacReasonUseCasesActions = { ...actions, ...extraActions };
export const gacReasonUseCasesReducer = slice.reducer;
